import { z } from "zod";
import { CommitShaSchema, PrincipalSchema, TimestampSchema, WpIdSchema } from "./primitives.ts";

/**
 * One piece of evidence behind a criterion result.
 * - `file`: a location in the diff (`ref` is the path, `line` optional).
 * - `test`: a test (`ref` is the test name, `detail` the outcome).
 * - `url`: behaviour observed at a preview URL (`ref` is the URL).
 * - `log`: a pointer to a log (`ref` is a URL or storage key).
 * - `note`: free-text reasoning, for example what is missing.
 */
export const EvidenceSchema = z.object({
  kind: z.enum(["file", "test", "url", "log", "note"]),
  /** What the evidence points at; see the kinds above. */
  ref: z.string().min(1),
  /** Line number for `file` evidence. */
  line: z.number().int().positive().optional(),
  /** What was observed. */
  detail: z.string().optional(),
});
/** See {@link EvidenceSchema}. */
export type Evidence = z.infer<typeof EvidenceSchema>;

/** The verifier's finding for one acceptance criterion. */
export const CriterionResultSchema = z.object({
  /** The acceptance criterion id from the work package, for example `AC-1`. */
  criterionId: z.string().min(1),
  /** True when the criterion is met. */
  met: z.boolean(),
  /** Why the verifier decided so. At least one item. On a fail, say what is missing. */
  evidence: z.array(EvidenceSchema).min(1),
});
/** See {@link CriterionResultSchema}. */
export type CriterionResult = z.infer<typeof CriterionResultSchema>;

/** Something this commit broke in a package that already landed. */
export const RegressionSchema = z.object({
  /** The landed package affected. */
  wpId: WpIdSchema,
  /** Its criterion that no longer holds. */
  criterionId: z.string().min(1),
  /** What broke. */
  detail: z.string().min(1),
});
/** See {@link RegressionSchema}. */
export type Regression = z.infer<typeof RegressionSchema>;

/**
 * The verifier's judgement of one commit of one work package, criterion by criterion.
 * `pass` is true only when every criterion is met and there are no regressions.
 */
export const VerdictSchema = z
  .object({
    /** The package judged. */
    wpId: WpIdSchema,
    /** The commit judged. */
    commit: CommitShaSchema,
    /** Overall result. */
    pass: z.boolean(),
    /** One result per acceptance criterion of the package. */
    results: z.array(CriterionResultSchema).min(1),
    /** Breaks to already-landed packages. Empty when none. */
    regressions: z.array(RegressionSchema),
    /** Anything else the agent should know, for example how to fix a failure. */
    notes: z.string(),
    /** Who produced it, usually `system:verifier`. */
    verifiedBy: PrincipalSchema,
    /** When it was produced. */
    createdAt: TimestampSchema,
  })
  .refine((v) => v.pass === (v.results.every((r) => r.met) && v.regressions.length === 0), {
    message: "pass must be true exactly when every criterion is met and there are no regressions",
    path: ["pass"],
  })
  .refine((v) => new Set(v.results.map((r) => r.criterionId)).size === v.results.length, {
    message: "one result per criterion",
    path: ["results"],
  });
/** See {@link VerdictSchema}. */
export type Verdict = z.infer<typeof VerdictSchema>;
